import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { Skeleton } from "@/components/Skeleton";
import {
  FilterBar,
  filtersToBody,
  hasAnyFilter,
  type Filters,
} from "@/components/MarketShared";
import type { TeamMember } from "@/types";

/** Live status shape returned by GET /api/market/publish-campaign/{id}. */
interface CampaignStatus {
  id: string;
  status:
    | "running"
    | "done"
    | "exhausted"
    | "completed_short"
    | "stopped"
    | "error";
  target_count: number;
  succeeded: number;
  source_gone: number;
  other_failed: number;
  cancelled: number;
  inflight: number;
  total_spawned: number;
  scanned: number;
  allow_republish: boolean;
  use_ss: boolean;
  created_at: string;
  finished_at: string | null;
  error: string | null;
}

const TERMINAL = new Set([
  "done",
  "exhausted",
  "completed_short",
  "stopped",
  "error",
]);

const POLL_MS = 2000;

/**
 * Fully-automatic "publish until N" flow. The director sets a target count +
 * agents + filters and clicks Go; the backend scans for owner listings,
 * publishes them, and auto-refills deleted ones until N actually land. No
 * per-listing selection — contrast with ``BulkOwnerFinderModal`` (manual scan
 * → pick → publish).
 */
export function PublishCampaignModal({
  initialFilters = {} as Filters,
  baseBody = {},
  label = "Publish campaign",
  resumeCampaignId,
  onClose,
}: {
  /** Seeds the in-modal FilterBar from the parent page's current filters. */
  initialFilters?: Filters;
  /** Non-FilterBar request extras (exclude flags, sort, text, listed_since)
   *  merged under the live FilterBar selection — same contract as the scan
   *  modal. */
  baseBody?: Record<string, unknown>;
  label?: string;
  /** When set, the modal opens straight into the live-progress view for an
   *  already-running campaign (reopened from the "View campaign" pill) instead
   *  of the setup form. */
  resumeCampaignId?: string;
  onClose: () => void;
}) {
  const [localFilters, setLocalFilters] = useState<Filters>(initialFilters);
  const [target, setTarget] = useState<number>(50);
  const [allowRepublish, setAllowRepublish] = useState(false);
  const [useSs, setUseSs] = useState(true);
  const [mhPayBalance, setMhPayBalance] = useState(true);

  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());

  const [filtersOpen, setFiltersOpen] = useState(true);
  const [agentsOpen, setAgentsOpen] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Once launched, we hold the campaign id + poll its status. In resume mode
  // we're handed the id up front and go straight to the progress view.
  const [campaignId, setCampaignId] = useState<string | null>(
    resumeCampaignId ?? null,
  );
  const [statusData, setStatusData] = useState<CampaignStatus | null>(null);
  const [stopping, setStopping] = useState(false);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Load the team (active, non-owner — owners don't publish inventory).
  useEffect(() => {
    let cancelled = false;
    api<TeamMember[]>("/api/team")
      .then((r) => {
        if (cancelled) return;
        // Include owners: an owner can publish as themselves or as another
        // owner (the backend's ``can_manage_user`` governs who a given viewer
        // may actually assign — a director still can't pick the owner).
        setTeam(r.filter((m) => m.status === "active"));
      })
      .catch((e) => {
        if (cancelled) return;
        setTeamError(e instanceof Error ? e.message : "Failed to load team");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedTeam = useMemo(() => {
    if (!team) return null;
    return [...team].sort(
      (a, b) =>
        (a.placements_today ?? 0) - (b.placements_today ?? 0) ||
        (a.full_name || a.email).localeCompare(b.full_name || b.email),
    );
  }, [team]);

  const uncredentialled = useMemo(() => {
    if (!team) return [] as TeamMember[];
    return team.filter(
      (m) =>
        selectedAgents.has(m.id) &&
        (!m.has_myhome_creds || !m.has_myhome_phone),
    );
  }, [team, selectedAgents]);

  function toggleAgent(id: string) {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function autoPickAgents() {
    if (!sortedTeam) return;
    const credentialled = sortedTeam.filter(
      (m) => m.has_myhome_creds && m.has_myhome_phone,
    );
    setSelectedAgents(new Set(credentialled.map((m) => m.id)));
  }

  // ── polling ──
  const poll = useCallback(async (id: string) => {
    try {
      const s = await api<CampaignStatus>(
        `/api/market/publish-campaign/${id}`,
      );
      setStatusData(s);
      if (TERMINAL.has(s.status) && pollRef.current != null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch {
      // transient — keep polling; a persistent failure just stalls the bar
    }
  }, []);

  useEffect(() => {
    if (!campaignId) return;
    poll(campaignId);
    pollRef.current = window.setInterval(() => poll(campaignId), POLL_MS);
    return () => {
      if (pollRef.current != null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [campaignId, poll]);

  const canSubmit =
    selectedAgents.size > 0 &&
    uncredentialled.length === 0 &&
    target >= 1 &&
    !submitting;

  async function start() {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const orderedAgents = (sortedTeam ?? [])
        .filter((m) => selectedAgents.has(m.id))
        .map((m) => m.id);
      const r = await api<{ campaign_id: string }>(
        "/api/market/publish-campaign",
        {
          method: "POST",
          json: {
            target_count: target,
            agent_user_ids: orderedAgents,
            // Same inventory the scan would sweep: page extras underneath the
            // live FilterBar selection on top.
            filters: { ...baseBody, ...filtersToBody(localFilters) },
            use_ss: useSs,
            mh_pay_balance: mhPayBalance,
            allow_republish: allowRepublish,
          },
        },
      );
      setCampaignId(r.campaign_id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setSubmitError(
          "A publish campaign is already running for your agency. " +
            "Wait for it to finish (or stop it) before starting another.",
        );
      } else {
        setSubmitError(
          err instanceof ApiError
            ? err.message
            : "Failed to start campaign — try again",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function stop() {
    if (!campaignId) return;
    setStopping(true);
    try {
      await api(`/api/market/publish-campaign/${campaignId}/stop`, {
        method: "POST",
      });
      poll(campaignId);
    } catch {
      /* ignore — next poll reflects state */
    } finally {
      setStopping(false);
    }
  }

  const launched = campaignId != null;
  const s = statusData;
  const isTerminal = s != null && TERMINAL.has(s.status);
  const landed = s?.succeeded ?? 0;
  const pct = s ? Math.min(100, (landed / Math.max(1, s.target_count)) * 100) : 0;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-stretch sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full sm:max-w-2xl sm:max-h-[92vh] sm:rounded-2xl bg-ink-950 border border-white/10 shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── header ── */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-white/5">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-slate-500">
              Publish until N
            </div>
            <div className="text-base font-semibold text-slate-100 truncate">
              {label}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Set how many owner listings to publish. We scan, publish, and
              auto-replace any that get deleted mid-run until the target lands —
              no manual picking.
            </div>
          </div>
          <button
            type="button"
            className="text-slate-500 hover:text-slate-200 p-1 -m-1"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* ── running / terminal progress view ── */}
        {launched ? (
          <div className="p-4 space-y-4 overflow-y-auto">
            <div>
              <div className="flex items-baseline justify-between mb-1.5">
                <div className="text-sm text-slate-300">
                  <span className="text-2xl font-semibold text-slate-100 tabular-nums">
                    {landed}
                  </span>{" "}
                  / {s?.target_count ?? target} landed
                </div>
                <StatusBadge status={s?.status ?? "running"} />
              </div>
              <div className="relative h-2 w-full rounded-full bg-white/[0.06] overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-accent-500 to-accent-400 transition-[width] duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              <Stat label="In flight" value={s?.inflight ?? 0} />
              <Stat
                label="Replaced (deleted)"
                value={s?.source_gone ?? 0}
                tone="amber"
              />
              <Stat label="Failed" value={s?.other_failed ?? 0} tone="rose" />
              <Stat label="Spawned" value={s?.total_spawned ?? 0} />
              <Stat label="Cancelled" value={s?.cancelled ?? 0} />
              <Stat label="Scanned" value={s?.scanned ?? 0} />
            </div>

            {s?.status === "exhausted" && (
              <div className="text-amber-200 text-xs bg-amber-500/10 border border-amber-500/25 rounded p-2">
                Ran out of matching owner listings before reaching the target —
                landed {landed} of {s.target_count}. Try widening the filters
                {allowRepublish ? "" : " or enabling republish"}.
              </div>
            )}
            {s?.status === "completed_short" && (
              <div className="text-amber-200 text-xs bg-amber-500/10 border border-amber-500/25 rounded p-2">
                Finished at {landed} of {s.target_count}. {s.other_failed}{" "}
                publish{s.other_failed === 1 ? "" : "es"} failed for reasons we
                don't auto-retry (portal rejection / credentials).
              </div>
            )}
            {s?.status === "error" && s.error && (
              <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
                {s.error}
              </div>
            )}
            {s?.status === "done" && (
              <div className="text-emerald-200 text-xs bg-emerald-500/10 border border-emerald-500/25 rounded p-2">
                Done — {landed} listing{landed === 1 ? "" : "s"} published.
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              {!isTerminal && (
                <button
                  type="button"
                  onClick={stop}
                  disabled={stopping}
                  className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/15 text-rose-200 text-sm px-3 h-9 transition disabled:opacity-50"
                >
                  ■ {stopping ? "Stopping…" : "Stop campaign"}
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary text-sm"
                title={
                  isTerminal
                    ? "Close"
                    : "Close this view — the campaign keeps running in the background"
                }
              >
                {isTerminal ? "Close" : "Close (keeps running)"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3 overflow-y-auto">
            {/* ── target ── */}
            <div className="flex items-center gap-3">
              <label className="text-sm text-slate-300 font-medium">
                Publish
              </label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={2000}
                value={target}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  setTarget(
                    Number.isFinite(n) && n > 0
                      ? Math.min(2000, Math.floor(n))
                      : 1,
                  );
                }}
                className="input !py-1 !text-sm h-9 w-24 text-right tabular-nums"
              />
              <span className="text-sm text-slate-400">
                owner listing{target === 1 ? "" : "s"}
              </span>
            </div>

            {/* ── property filters ── */}
            <div className="border border-white/5 rounded-lg bg-ink-950/60">
              <button
                type="button"
                onClick={() => setFiltersOpen((v) => !v)}
                className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-300 transition px-3 py-2"
              >
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform ${filtersOpen ? "rotate-90" : ""}`}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                Property filters
                {!filtersOpen && hasAnyFilter(localFilters) && (
                  <span className="text-accent-300 normal-case tracking-normal">
                    · active
                  </span>
                )}
              </button>
              {filtersOpen && (
                <div className="px-3 pb-3">
                  <FilterBar
                    filters={localFilters}
                    onChange={setLocalFilters}
                    hideOwnerDirect
                  />
                </div>
              )}
            </div>

            {/* ── agents ── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={() => setAgentsOpen((v) => !v)}
                  className="label !mb-0 text-xs flex items-center gap-1.5 hover:text-slate-200 transition"
                >
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform ${agentsOpen ? "rotate-90" : ""}`}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                  Agents{" "}
                  <span className="text-slate-600 font-normal">
                    {selectedAgents.size > 0
                      ? `· ${selectedAgents.size} selected`
                      : "· round-robin target"}
                  </span>
                </button>
                <button
                  type="button"
                  className="btn-secondary !py-1 !text-xs"
                  onClick={autoPickAgents}
                  disabled={(team?.length ?? 0) === 0}
                >
                  ⚡ Auto-pick
                </button>
              </div>
              {!agentsOpen ? null : teamError ? (
                <div className="text-rose-300 text-xs">{teamError}</div>
              ) : sortedTeam === null ? (
                <Skeleton className="h-10" />
              ) : sortedTeam.length === 0 ? (
                <div className="text-xs text-slate-500">
                  No active team members.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto -mr-2 pr-2">
                  {sortedTeam.map((m) => {
                    const on = selectedAgents.has(m.id);
                    const credsOk = m.has_myhome_creds && m.has_myhome_phone;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => toggleAgent(m.id)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition ${
                          !credsOk
                            ? on
                              ? "border-rose-500/50 bg-rose-500/10 text-rose-200"
                              : "border-rose-500/30 bg-rose-500/[0.04] text-rose-300/80 hover:border-rose-500/50"
                            : on
                              ? "border-accent-500/50 bg-accent-500/10 text-accent-200"
                              : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20 hover:text-slate-100"
                        }`}
                      >
                        <Avatar
                          url={m.avatar_url}
                          name={m.full_name}
                          email={m.email}
                          size="xs"
                          online={m.is_online}
                        />
                        <span className="truncate max-w-[130px]">
                          {m.full_name || m.email}
                        </span>
                        <span className="text-[10px] tabular-nums px-1.5 py-0.5 rounded-full bg-white/[0.06] text-slate-300">
                          {m.placements_today ?? 0}
                        </span>
                        {!credsOk && (
                          <span className="text-[10px] text-rose-300" title="MyHome credentials missing">
                            ⛔
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {uncredentialled.length > 0 && (
              <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
                ⛔ {uncredentialled.length} picked agent
                {uncredentialled.length === 1 ? "" : "s"} missing MyHome creds —
                deselect to continue.
              </div>
            )}

            {/* ── options ── */}
            <div className="flex flex-wrap items-center gap-4 pt-1">
              <label className="text-xs text-slate-300 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowRepublish}
                  onChange={(e) => setAllowRepublish(e.target.checked)}
                />
                Allow republishing already-placed (via a different agent)
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mhPayBalance}
                  onChange={(e) => setMhPayBalance(e.target.checked)}
                />
                MyHome balance
              </label>
              <label className="text-xs text-slate-400 inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useSs}
                  onChange={(e) => setUseSs(e.target.checked)}
                />
                Also SS.ge
              </label>
            </div>

            {submitError && (
              <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
                {submitError}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="btn-secondary text-sm">
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary text-sm"
                disabled={!canSubmit}
                onClick={start}
                title={
                  !canSubmit
                    ? "Pick at least one credentialled agent and a target ≥ 1"
                    : `Publish ${target} owner listing${target === 1 ? "" : "s"} across ${selectedAgents.size} agent${selectedAgents.size === 1 ? "" : "s"}`
                }
              >
                {submitting
                  ? "Starting…"
                  : `▶ Publish ${target} → ${selectedAgents.size || "?"}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "amber" | "rose";
}) {
  const color =
    tone === "amber"
      ? "text-amber-300"
      : tone === "rose"
        ? "text-rose-300"
        : "text-slate-200";
  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1.5">
      <div className={`text-lg font-semibold tabular-nums ${color}`}>
        {value.toLocaleString()}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">
        {label}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: CampaignStatus["status"] }) {
  const map: Record<CampaignStatus["status"], [string, string]> = {
    running: ["Running", "text-accent-200 bg-accent-500/15 border-accent-500/30"],
    done: ["Done", "text-emerald-200 bg-emerald-500/15 border-emerald-500/30"],
    exhausted: ["Out of stock", "text-amber-200 bg-amber-500/15 border-amber-500/30"],
    completed_short: [
      "Finished short",
      "text-amber-200 bg-amber-500/15 border-amber-500/30",
    ],
    stopped: ["Stopped", "text-slate-300 bg-white/[0.06] border-white/10"],
    error: ["Error", "text-rose-200 bg-rose-500/15 border-rose-500/30"],
  };
  const [text, cls] = map[status];
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 border ${cls}`}
    >
      {status === "running" && (
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      )}
      {text}
    </span>
  );
}
